import * as React from 'react';
import { connect } from 'react-redux';
import { Trans, withTranslation } from 'react-i18next';
import { select } from '@rematch/select';

import { AppLayout } from '../../layouts/AppLayout';
import { dispatch } from '../../store';
import { Loader } from '../../atoms/loader';
import { Box } from '../../atoms/box';
import { Text } from '../../atoms/text';
import { CoursesList } from './CoursesList';
import { SegmentedControl } from './SegmentedControl';
import { showSuccessErrorAlert } from '../../atoms/alert';
import { CoursesService } from '../../services/CoursesService';
import { CourseCard } from '../../atoms/course-card';
import { CourseDuplicate } from '../../atoms/course-duplicate';
import { CourseHeader } from '../../atoms/course-header';
import { can } from '../../utils/permissions';
import { Card } from 'ui';

export class Renderer extends React.Component {
  state = {
    selectedTab: 'all',
    joinedCourses: [],
    featuredCourses: null,
    activeProgram: null,
    openModal: false,
  };

  COURSE_STATUSES = [
    { value: 'all', title: this.props.t('All programs'), scope: '' },
    { value: 'joined', title: this.props.t('My programs'), scope: '' },
  ];

  COURSE_STATUSES_ADMIN = [
    { value: 'all', title: this.props.t('All programs'), scope: '' },
    { value: 'draft', title: this.props.t('Draft'), scope: 'draft' },
    { value: 'published', title: this.props.t('Published'), scope: 'published' },
    { value: 'archived', title: this.props.t('Archived'), scope: 'finished' },
    { value: 'joined', title: this.props.t('My programs'), scope: '' },
  ];

  componentDidMount() {
    this.fetchData();
  }

  fetchData = async () => {
    await this.props.getCategory(this.props.category_id);
    this.initCourses(this.state.selectedTab);
  };

  initCourses = async (scope) => {
    const selectedTab = this.COURSE_STATUSES_ADMIN.find((item) => {
      return item.value === scope;
    });
    await dispatch.courses.get({ category_id: this.props.category_id, status: selectedTab.scope });
    if (scope === 'all') {
      const featuredCourses = await this.props.featuredCourses();
      this.setState({
        joinedCourses: this.props.coursesByCategory.filter((course) => can(course, 'Course', 'isMemberOfProgram')),
        featuredCourses: featuredCourses.data,
      });
    }
  };

  onCourseClick = (course) => {
    window.location.href = `/courses/${course.id}`;
  };

  renderCategoryHeader = () => {
    return (
      <Card shrinked>
        <Box justifyContent="center" alignItems="center" className="program-category-header">
          <img src={this.props.category?.symbol_photo?.url} alt="" className="program-category-header__logo" />
          <div className="program-category-header__heading">
            <Text size="35px" weight="600">
              {this.props.category?.name}
            </Text>
          </div>
          <SegmentedControl
            labels={can(undefined, 'Course', 'create') ? [...this.COURSE_STATUSES_ADMIN] : [...this.COURSE_STATUSES]}
            selectedLabel={this.state.selectedTab}
            onChange={(label) => {
              this.setState({ selectedTab: label });
              if (label !== 'joined') {
                this.setState(
                  {
                    featuredCourses: null,
                  },
                  () => this.initCourses(label),
                );
              }
            }}
          />
        </Box>
      </Card>
    );
  };

  deleteCourse = async (course) => {
    if (await window.confirm(this.props.t('Are you sure you want to delete this program?'))) {
      const response = await this.props.deleteCourse(course);
      showSuccessErrorAlert({
        isOk: response.ok,
        successMessage: <Trans>Program successfully deleted.</Trans>,
        errorMessage: response?.data?.message,
      });
    }
  };

  publishCourse = async (course) => {
    if (await window.confirm(this.props.t('Are you sure you want to publish this program?'))) {
      const result = { id: course.id, status: 'published' };
      const response = await this.props.updateCourseStatus(result);
      showSuccessErrorAlert({
        isOk: response.ok,
        successMessage: <Trans>The program has been published.</Trans>,
        errorMessage: response?.data?.message,
      });
    }
  };

  finishCourse = async (course) => {
    if (await window.confirm(this.props.t('Are you sure you want to archive this program?'))) {
      const result = { id: course.id, status: 'finished' };
      const response = await this.props.updateCourseStatus(result);
      showSuccessErrorAlert({
        isOk: response.ok,
        successMessage: <Trans>The program has been finished.</Trans>,
        errorMessage: response?.data?.message,
      });
    }
  };

  reportCourse = async (course) => {
    if (await window.confirm(this.props.t('Are you sure you want to report this program?'))) {
      CoursesService.reportAbuse(course.id);
    }
  };

  duplicateProgram = (program) => {
    this.setState({ openModal: true, activeProgram: program });
  };

  closeModal = () => {
    this.setState({ openModal: false });
  };

  redirect = (path) => {
    this.props.history.push(path);
  };

  onHandleJoin = async () => {
    if (this.state.selectedTab === 'all') {
      const featuredCourses = await this.props.featuredCourses();
      this.setState({
        featuredCourses: featuredCourses.data,
      });
    }
  };

  renderFeaturedCourses = () => {
    return (
      <React.Fragment>
        {this.state.featuredCourses && this.state.featuredCourses.length > 0 && (
          <div>
            <h2>
              <Trans>Featured Programs</Trans>
            </h2>
            <div className="featured-programs">
              {this.state.featuredCourses.map((course) => (
                <div key={course.id} className="featured-programs__program">
                  <CourseCard
                    course={course}
                    viewer={this.props.user}
                    onRequestCourse={() => this.props.history.push(`/courses/${course.id}/about`)}
                    onEditCourse={() => this.props.history.push(`/courses/${course.id}/update`)}
                    onDeleteCourse={() => this.deleteCourse(course)}
                    onFinishCourse={() => this.finishCourse(course)}
                    onPublishCourse={() => this.publishCourse(course)}
                    onReportCourse={() => this.reportCourse(course)}
                    onDuplicateCourse={() => this.duplicateProgram(course)}
                    onHandleJoin={this.onHandleJoin}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </React.Fragment>
    );
  };

  renderCourses = () => {
    const courses = this.state.selectedTab === 'joined' ? this.state.joinedCourses : this.props.coursesByCategory;
    return (
      <div>
        {this.props.loadingCourses && !this.state.featuredCourses ? (
          <Loader />
        ) : (
          <React.Fragment>
            {this.state.selectedTab === 'all' && (
              <React.Fragment>
                {this.renderFeaturedCourses()}
                <h2>
                  <Trans>All Programs</Trans>
                </h2>
              </React.Fragment>
            )}
            <CoursesList
              deleteCourse={this.deleteCourse}
              finishCourse={this.finishCourse}
              duplicateCourse={this.duplicateProgram}
              reportCourse={this.reportCourse}
              publishCourse={this.publishCourse}
              courses={courses}
              onHandleJoin={this.onHandleJoin}
            />
          </React.Fragment>
        )}
      </div>
    );
  };

  render() {
    const { user } = this.props;

    return (
      <AppLayout
        center={
          <React.Fragment>
            <CourseHeader user={user} history={this.props.history} showBackButton />
            {this.props.loadingCategory ? (
              <Loader />
            ) : (
              <>
                <div>{this.renderCategoryHeader()}</div>
                <div>{this.renderCourses()}</div>
              </>
            )}
            <CourseDuplicate
              isDuplicateOpen={this.state.openModal}
              onRequestClose={this.closeModal}
              program={this.state.activeProgram}
              redirect={this.redirect}
            />
          </React.Fragment>
        }
      />
    );
  }
}

const mapState = (state, props) => {
  return {
    category_id: props.match.params.categoryId,
    coursesByCategory: select.courses.get(state, { category_id: props.match.params.categoryId }),
    category: select.categories.find(state, props.match.params.categoryId),
    loadingCourses: state.loading.effects.courses.get,
    loadingCategory: state.loading.effects.categories.find,
    user: select.session.user(state),
  };
};

const mapDispatch = (dispatch, props) => ({
  getCategory: (id) => dispatch.categories.find(id),
  deleteCourse: (course) => dispatch.adminCourses.delete(course),
  updateCourseStatus: (course) => dispatch.courses.updateStatus(course),
  featuredCourses: () => dispatch.courses.get({ category_id: props.match.params.categoryId, pinned: true }),
});

export const PageCourseCategory = withTranslation()(connect(mapState, mapDispatch)(Renderer));
